import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cardDraggableId, type ColumnKey } from "./ids";

interface SortableCardProps {
  column: ColumnKey;
  contactId: string;
  children: ReactNode;
  className?: string;
}

// Generic draggable card shell: dnd-kit wiring + whole-card navigation.
// The card content comes in as children (PipelineCardBody).
export function SortableCard({
  column,
  contactId,
  children,
  className = "",
}: SortableCardProps) {
  const navigate = useNavigate();
  const id = cardDraggableId(column, contactId);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { column, contactId },
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: "pointer",
  };

  // Whole-card click → navigate to contact detail.
  // dnd-kit's PointerSensor (distance:6) suppresses the native click when a
  // drag activates, so tap vs drag separates itself. We still guard against
  // clicks that originated on an interactive descendant (button/link) so
  // their handlers win even if a future sibling forgets stopPropagation.
  function handleCardClick(e: React.MouseEvent<HTMLElement>) {
    const target = e.target as HTMLElement;
    if (target.closest("button, a")) return;
    navigate(`/contacts/${contactId}`);
  }

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`kcard ${isDragging ? "is-picked" : ""} ${className}`.trim()}
      onClick={handleCardClick}
      {...attributes}
      {...listeners}
    >
      {children}
    </article>
  );
}
